import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { ValveStateFieldsWithDefaults, ValveStateSchemaTypes } from "../types/valveState.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "ValveState" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<ValveState>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ValveState extends SchemaBase<ValveState, ValveStateFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({
        zodValidator: z.union([z.tuple([z.boolean(), z.boolean()]), z.tuple([z.boolean()])]),
        responseOnly: true,
    })
    @prop({ type: () => Boolean, required: true }, PropType.ARRAY)
    open!: [boolean, boolean] | [boolean];

    @validate({
        zodValidator: z.union([z.tuple([z.boolean(), z.boolean()]), z.tuple([z.boolean()])]),
        responseOnly: true,
    })
    @prop({ type: () => Boolean, required: true }, PropType.ARRAY)
    automating!: [boolean, boolean] | [boolean];

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: false })
    manualOverrideEngaged?: boolean;

    static getters = {
        getEncodedValue: function encode(self: ValveState | { open: [boolean, boolean] }) {
            // Read each property directly from `self` rather than using Object.assign.
            // Object.assign only copies own enumerable properties, but on a Mongoose hydrated
            // subdocument `open` and `automating` are exposed via prototype getters, not own
            // properties — so Object.assign would silently fall back to the default [false, false]
            // regardless of the actual valve state.
            let intValveState = 0;
            if (self?.open?.[0]) {
                intValveState |= 1 << 0;
            }
            if (self?.open?.[1]) {
                intValveState |= 1 << 1;
            }
            if ((self as ValveState)?.automating?.[0]) {
                intValveState |= 1 << 2;
            }
            if ((self as ValveState)?.automating?.[1]) {
                intValveState |= 1 << 3;
            }
            if ((self as ValveState)?.manualOverrideEngaged) {
                intValveState |= 1 << 4;
            }
            return intValveState;
        },
    } as const satisfies Record<string, (self: ValveState) => any>;

    static utilities = {
        decode: function decodeValveState(vs: number): ValveState {
            return new ValveState({
                open: [!!(vs & (1 << 0)), !!(vs & (1 << 1))],
                automating: [!!(vs & (1 << 2)), !!(vs & (1 << 3))],
                manualOverrideEngaged: !!(vs & (1 << 4)),
            });
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ValveStateSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ValveState, ValveStateFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ValveState.dtoValidators = getZodSchemaFromClass(ValveState) as unknown as DtoValidatorTypes<ValveStateSchemaTypes>;
